// Boxes: the Raspberry Pi call boxes, their modems, and approving or retiring them.
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CircleCheck, Eye, Pause, Pencil, PhoneOutgoing, Power, PowerOff, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import {
  useVoiceBoxPortsQuery,
  useVoiceBoxQuery,
  useVoiceBoxUpdateMutation,
  useVoiceBoxesQuery,
  useVoiceCallsQuery,
  useVoicePortUpdateMutation,
  useVoiceRefresh,
  useVoiceTestCallMutation,
  TestCallResult,
} from '@/client/api/voice/voice.query';
import { BoxPatch, BoxStatus, VoiceBox, VoicePort } from '@/client/types/voice';
import { Card, EmptyRow, Field, FormDialog, FormField, INPUT_CLASS, PageShell, Th } from '../Cloud/cloudUi';
import {
  AccessDenied,
  BoxStatusPill,
  ErrorNotice,
  FetchingChip,
  Kebab,
  KebabItem,
  LoadingNote,
  OnlineDot,
  VOICE_BASE,
  VoicePerms,
  fmtWhen,
  freeModems,
  signalText,
  titleCase,
  useVoicePerms,
} from './voiceUi';
import { CallSheet, CallsTable } from './VoiceCallsPage';

const SKU_NAME: Record<string, string> = { 'pi-sim7600-2': '2-modem box' };
const skuName = (sku?: string | null) => (sku ? SKU_NAME[sku] ?? sku : '—');
const boxPath = (id: string) => `${VOICE_BASE}/boxes/${encodeURIComponent(id)}`;

/* ---------- confirmations ---------- */

type BoxAction =
  | { kind: 'status'; box: VoiceBox; to: Exclude<BoxStatus, 'pending'> }
  | { kind: 'edit'; box: VoiceBox }
  | { kind: 'test'; box: VoiceBox };

const E164 = /^\+[1-9]\d{7,14}$/;

/** Any country in international form. A Ghana local number (0 + 9 digits) becomes
 *  +233; a 00 prefix becomes +. Spaces, dashes and brackets are ignored. */
export const toE164 = (raw: string): string => {
  let n = raw.replace(/[\s().-]/g, '');
  if (n.startsWith('00')) n = `+${n.slice(2)}`;
  else if (/^0\d{9}$/.test(n)) n = `+233${n.slice(1)}`;
  return n;
};

/** Open a call window for the admin's own phones: call in from one, the box rings the other. */
const TestCallDialog: React.FC<{ box: VoiceBox; onClose: () => void }> = ({ box, onClose }) => {
  const m = useVoiceTestCallMutation();
  const navigate = useNavigate();
  const [caller, setCaller] = useState('');
  const [destination, setDestination] = useState('');
  const [result, setResult] = useState<TestCallResult | null>(null);
  const label = box.name || box.box_id;
  const from = toE164(caller);
  const to = toE164(destination);
  if (result) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test call ready on {label}</DialogTitle>
            <DialogDescription>Call this number from {from}. The box rings {to} once you are through.</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
            <div className="text-xs uppercase tracking-wide text-gray-500">Dial</div>
            <div className="mt-1 font-mono text-2xl font-semibold">{result.call_number ?? 'unknown'}</div>
            <div className="mt-2 text-sm text-gray-600">Open until {fmtWhen(result.expires_at)}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate(`${VOICE_BASE}/calls`)}>Watch it in Calls</Button>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  const valid = E164.test(from) && E164.test(to) && from !== to;
  return (
    <FormDialog
      title={`Place a test call on ${label}`}
      description="Opens a normal call window: you call in from the first number, the box rings the second, plays the announcements and connects you. The call is recorded."
      confirmLabel="Open call window"
      valid={valid}
      busy={m.isPending}
      error={m.error}
      onClose={onClose}
      onConfirm={(reason) =>
        m.mutate(
          { boxId: box.box_id, callerNumber: from, destination: to, reason },
          { onSuccess: (r) => setResult(r) },
        )
      }
    >
      <FormField label="Call in from" hint="Your phone, any country, with its country code, for example +233 20 000 0000. A Ghana number starting with 0 is fine too. Only this number is let in.">
        <input value={caller} onChange={(e) => setCaller(e.target.value)} placeholder="+233 20 000 0000" inputMode="tel" className={`${INPUT_CLASS} mt-1 w-full font-mono`} />
        {caller && from !== caller.trim() && E164.test(from) && <p className="mt-1 text-xs text-gray-500">Will use {from}</p>}
      </FormField>
      <FormField label="Number to ring" hint="The phone that plays the seller. Must differ from the first.">
        <input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="+233 20 000 0000" inputMode="tel" className={`${INPUT_CLASS} mt-1 w-full font-mono`} />
        {destination && to !== destination.trim() && E164.test(to) && <p className="mt-1 text-xs text-gray-500">Will use {to}</p>}
      </FormField>
    </FormDialog>
  );
};

const STATUS_COPY: Record<Exclude<BoxStatus, 'pending'>, { title: string; text: string; confirm: string; done: string }> = {
  active: {
    title: 'Activate this box',
    text: 'The box starts taking calls as soon as its next heartbeat arrives (within 30 seconds).',
    confirm: 'Activate',
    done: 'Box activated',
  },
  draining: {
    title: 'Drain this box',
    text: 'No new calls go to this box. Calls already in progress finish normally.',
    confirm: 'Drain',
    done: 'Box draining',
  },
  disabled: {
    title: 'Disable this box',
    text: 'No new calls go to this box and it is asked to wind down. Calls already in progress are not cut.',
    confirm: 'Disable',
    done: 'Box disabled',
  },
};

const BoxActionDialog: React.FC<{ action: BoxAction; onClose: () => void }> = ({ action, onClose }) => {
  const m = useVoiceBoxUpdateMutation();
  const box = action.box;
  const [name, setName] = useState(box.name ?? '');
  const [url, setUrl] = useState(box.base_url ?? '');
  const [notes, setNotes] = useState(box.notes ?? '');
  const label = box.name || box.box_id;
  if (action.kind === 'test') return <TestCallDialog box={box} onClose={onClose} />;
  if (action.kind === 'status') {
    const c = STATUS_COPY[action.to];
    return (
      <FormDialog
        title={`${c.title}: ${label}`}
        description={c.text}
        confirmLabel={c.confirm}
        destructive={action.to !== 'active'}
        busy={m.isPending}
        error={m.error}
        onClose={onClose}
        onConfirm={(reason) =>
          m.mutate({ boxId: box.box_id, reason, patch: { status: action.to } }, { onSuccess: () => { toast.success(c.done); onClose(); } })
        }
      />
    );
  }
  const validUrl = url === '' || /^https?:\/\/\S+$/.test(url);
  return (
    <FormDialog
      title={`Edit ${label}`}
      confirmLabel="Save"
      valid={name.trim().length > 0 && validUrl}
      busy={m.isPending}
      error={m.error}
      onClose={onClose}
      onConfirm={(reason) => {
        const patch: BoxPatch = { name: name.trim(), notes };
        if (url.trim()) patch.base_url = url.trim();
        m.mutate({ boxId: box.box_id, reason, patch }, { onSuccess: () => { toast.success('Box saved'); onClose(); } });
      }}
    >
      <FormField label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full`} />
      </FormField>
      <FormField label="Base URL" hint="Where the cloud reaches the box, for example http://10.0.0.20:5063. Defaults to the address the box reports.">
        <input value={url} onChange={(e) => setUrl(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full font-mono`} />
      </FormField>
      {!validUrl && <p className="text-sm text-red-600">The URL must start with http:// or https://.</p>}
      <FormField label="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-blue-400" />
      </FormField>
    </FormDialog>
  );
};

export const boxMenu = (
  box: VoiceBox,
  perms: VoicePerms,
  nav: (to: string) => void,
  act: (a: BoxAction) => void,
  withView = true,
): KebabItem[] => [
  { label: 'View', icon: <Eye className="h-4 w-4" />, hidden: !withView, onSelect: () => nav(boxPath(box.box_id)) },
  { label: 'Place test call', icon: <PhoneOutgoing className="h-4 w-4" />, hidden: !perms.canManage || box.status !== 'active' || !box.online, onSelect: () => act({ kind: 'test', box }) },
  { label: 'Activate', icon: <Power className="h-4 w-4" />, hidden: !perms.canManage || box.status === 'active', onSelect: () => act({ kind: 'status', box, to: 'active' }) },
  { label: 'Drain', icon: <Pause className="h-4 w-4" />, hidden: !perms.canManage || box.status !== 'active', onSelect: () => act({ kind: 'status', box, to: 'draining' }) },
  { label: 'Disable', icon: <PowerOff className="h-4 w-4" />, danger: true, hidden: !perms.canManage || box.status === 'disabled', onSelect: () => act({ kind: 'status', box, to: 'disabled' }) },
  { label: 'Edit', icon: <Pencil className="h-4 w-4" />, hidden: !perms.canManage, onSelect: () => act({ kind: 'edit', box }) },
];

const PendingNote: React.FC<{ box: VoiceBox; perms: VoicePerms; act: (a: BoxAction) => void }> = ({ box, perms, act }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
    <span>
      <strong>New box waiting for approval.</strong> {box.name || box.box_id} has reported in but takes no calls until it is activated.
    </span>
    {perms.canManage && (
      <Button size="sm" onClick={() => act({ kind: 'status', box, to: 'active' })}>
        <CircleCheck className="mr-2 h-3.5 w-3.5" /> Activate
      </Button>
    )}
  </div>
);

/* ---------- list ---------- */

export const VoiceBoxesPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Voice calling', href: VOICE_BASE }, { label: 'Boxes' }]);
  const perms = useVoicePerms();
  const navigate = useNavigate();
  const refresh = useVoiceRefresh();
  const q = useVoiceBoxesQuery();
  const [action, setAction] = useState<BoxAction | null>(null);

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Boxes" description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }
  const boxes = q.data?.boxes ?? [];
  return (
    <PageShell
      title="Boxes"
      description="The call boxes that connect buyers and sellers. Capacity grows by adding boxes."
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && <LoadingNote>Loading boxes and their last heartbeats.</LoadingNote>}
      {boxes.filter((b) => b.status === 'pending').map((b) => (
        <PendingNote key={b.box_id} box={b} perms={perms} act={setAction} />
      ))}
      <Card title="Boxes" flush>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
              <tr>
                <Th>Name</Th>
                <Th>Box id</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Connection</Th>
                <Th>Free modems</Th>
                <Th>Version</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {q.isLoading || boxes.length === 0 ? (
                <EmptyRow cols={8} loading={q.isLoading}>No box has reported in yet.</EmptyRow>
              ) : (
                boxes.map((b) => (
                  <tr key={b.box_id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(boxPath(b.box_id))}>
                    <td className="px-4 py-2 font-medium text-gray-900">{b.name || b.box_id}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">{b.box_id}</td>
                    <td className="px-4 py-2 text-gray-700">{skuName(b.sku)}</td>
                    <td className="px-4 py-2"><BoxStatusPill status={b.status} /></td>
                    <td className="px-4 py-2"><OnlineDot online={b.online} lastSeen={b.last_seen_at} /></td>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-700">{freeModems(b)}</td>
                    <td className="px-4 py-2 text-gray-700">{b.version ?? '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <Kebab name={b.name || b.box_id} items={boxMenu(b, perms, navigate, setAction)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {action && <BoxActionDialog action={action} onClose={() => setAction(null)} />}
    </PageShell>
  );
};

/* ---------- modems ---------- */

const portChannel = (p: VoicePort): string => {
  const x = p as unknown as Record<string, unknown>;
  return String(p.channel ?? x.name ?? x.id ?? '');
};

type PortAction = { port: VoicePort; kind: 'edit' | 'toggle' };

const PortDialog: React.FC<{ boxId: string; action: PortAction; onClose: () => void }> = ({ boxId, action, onClose }) => {
  const m = useVoicePortUpdateMutation();
  const { port, kind } = action;
  const channel = portChannel(port);
  const [msisdn, setMsisdn] = useState(port.msisdn ?? '');
  const [role, setRole] = useState(String(port.role ?? 'inbound'));
  const enabled = port.enabled !== false;
  if (kind === 'toggle') {
    return (
      <FormDialog
        title={`${enabled ? 'Disable' : 'Enable'} modem ${channel}`}
        description={enabled ? 'The modem stops taking part in calls. A call using it is not cut.' : 'The modem can take part in calls again.'}
        confirmLabel={enabled ? 'Disable' : 'Enable'}
        destructive={enabled}
        busy={m.isPending}
        error={m.error}
        onClose={onClose}
        onConfirm={(reason) =>
          m.mutate({ boxId, channel, reason, patch: { enabled: !enabled } }, { onSuccess: () => { toast.success(enabled ? 'Modem disabled' : 'Modem enabled'); onClose(); } })
        }
      />
    );
  }
  return (
    <FormDialog
      title={`Edit modem ${channel}`}
      description="Changing the number is how a SIM swap is recorded. Do this after the new SIM is in the modem."
      confirmLabel="Save"
      valid={/^\+?\d{6,15}$/.test(msisdn.trim())}
      busy={m.isPending}
      error={m.error}
      onClose={onClose}
      onConfirm={(reason) =>
        m.mutate({ boxId, channel, reason, patch: { msisdn: msisdn.trim(), role } }, { onSuccess: () => { toast.success('Modem saved'); onClose(); } })
      }
    >
      <FormField label="Phone number" hint="International format, for example +233201234567.">
        <input value={msisdn} onChange={(e) => setMsisdn(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full font-mono`} />
      </FormField>
      <FormField label="Role">
        <select value={role} onChange={(e) => setRole(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full`}>
          <option value="inbound">Inbound (buyers call this number)</option>
          <option value="outbound">Outbound (calls the seller)</option>
        </select>
      </FormField>
    </FormDialog>
  );
};

const STATE_DOT: Record<string, string> = {
  free: 'bg-green-500',
  busy: 'bg-blue-500',
  disabled: 'bg-slate-400',
  unhealthy: 'bg-red-500',
};

const ModemsCard: React.FC<{ box: VoiceBox; perms: VoicePerms }> = ({ box, perms }) => {
  const ports = useVoiceBoxPortsQuery(box.box_id);
  const [action, setAction] = useState<PortAction | null>(null);
  const live = ports.data ? (Array.isArray(ports.data) ? ports.data : ports.data.ports ?? []) : [];
  // The box's own answer wins; its last heartbeat covers a box that cannot be reached right now.
  const rows: VoicePort[] = live.length ? live : (box.channels ?? []);
  const usingHeartbeat = !live.length && rows.length > 0;
  return (
    <Card
      title="Modems"
      note={usingHeartbeat ? 'From the last heartbeat; the box did not answer' : undefined}
      flush
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
            <tr>
              <Th>Channel</Th>
              <Th>Role</Th>
              <Th>Number</Th>
              <Th>Enabled</Th>
              <Th>State</Th>
              <Th>Signal</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {ports.isLoading && !rows.length ? (
              <EmptyRow cols={7} loading>{null}</EmptyRow>
            ) : rows.length === 0 ? (
              <EmptyRow cols={7}>This box has not reported any modems.</EmptyRow>
            ) : (
              rows.map((p) => {
                const ch = portChannel(p);
                const sig = signalText(p);
                const enabled = p.enabled !== false;
                return (
                  <tr key={ch} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{ch}</td>
                    <td className="px-4 py-2 text-gray-700">{p.role ? titleCase(String(p.role)) : '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{p.msisdn || '—'}</td>
                    <td className="px-4 py-2 text-gray-700">{enabled ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-2">
                      {p.state ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-700">
                          <span className={`h-2 w-2 rounded-full ${STATE_DOT[String(p.state)] ?? 'bg-sky-500'}`} />
                          {titleCase(String(p.state))}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {sig ?? <span className="text-gray-300">—</span>}
                      {p.registered === false && <span className="ml-1 text-xs text-red-600">not on network</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {perms.canManage && (
                        <Kebab
                          name={ch}
                          items={[
                            { label: 'Edit number or role', icon: <Pencil className="h-4 w-4" />, onSelect: () => setAction({ port: p, kind: 'edit' }) },
                            { label: enabled ? 'Disable' : 'Enable', icon: enabled ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />, danger: enabled, onSelect: () => setAction({ port: p, kind: 'toggle' }) },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {action && <PortDialog boxId={box.box_id} action={action} onClose={() => setAction(null)} />}
    </Card>
  );
};

/* ---------- detail ---------- */

const healthText = (v: unknown): string =>
  v && typeof v === 'object'
    ? Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k} ${String(x)}`).join(', ')
    : String(v);

export const VoiceBoxDetailPage: React.FC = () => {
  const { boxId = '' } = useParams();
  const perms = useVoicePerms();
  const navigate = useNavigate();
  const refresh = useVoiceRefresh();
  const q = useVoiceBoxQuery(boxId);
  const calls = useVoiceCallsQuery({ box_id: boxId, per_page: 10 }, perms.canView && !!boxId);
  const [action, setAction] = useState<BoxAction | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const box = q.data?.box;
  useBreadcrumbs([
    { label: 'Voice calling', href: VOICE_BASE },
    { label: 'Boxes', href: `${VOICE_BASE}/boxes` },
    { label: box?.name || boxId },
  ]);

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Box" description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }
  return (
    <PageShell
      title={box?.name || boxId}
      description={box ? `${skuName(box.sku)} · ${box.box_id}` : ''}
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          {box && <Kebab name={box.name || box.box_id} items={boxMenu(box, perms, navigate, setAction, false)} />}
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading || !box ? (
        !q.isError && <LoadingNote>Loading this box and its modems.</LoadingNote>
      ) : (
        <>
          {box.status === 'pending' && <PendingNote box={box} perms={perms} act={setAction} />}
          <Card title="Health">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="Status"><BoxStatusPill status={box.status} /></Field>
              <Field label="Connection"><OnlineDot online={box.online} lastSeen={box.last_seen_at} /></Field>
              <Field label="Free modems">{freeModems(box)}</Field>
              <Field label="Calls now">{box.active_sessions ?? 0}{q.data?.calls_today != null ? ` (${q.data.calls_today} today)` : ''}</Field>
              <Field label="Version">{box.version ?? '—'}</Field>
              <Field label="Base URL"><span className="font-mono text-xs">{box.base_url ?? '—'}</span></Field>
              <Field label="Last heartbeat">{fmtWhen(box.last_seen_at)}</Field>
              <Field label="Changed">{box.updated_by ? `${fmtWhen(box.updated_at)} by ${box.updated_by}` : fmtWhen(box.updated_at)}</Field>
              {Object.entries(box.health ?? {}).map(([k, v]) => (
                <Field key={k} label={titleCase(k)}>{healthText(v)}</Field>
              ))}
            </div>
            {box.notes && <p className="mt-4 whitespace-pre-wrap text-sm text-gray-600">{box.notes}</p>}
          </Card>
          <ModemsCard box={box} perms={perms} />
          <Card
            title="Recent calls on this box"
            flush
            action={
              <Link to={`${VOICE_BASE}/calls`} className="text-xs text-blue-600 hover:underline">
                All calls
              </Link>
            }
          >
            <CallsTable
              calls={calls.data?.calls ?? []}
              loading={calls.isLoading}
              perms={perms}
              emptyText="No calls on this box yet."
              onOpen={setOpen}
            />
          </Card>
        </>
      )}
      {action && <BoxActionDialog action={action} onClose={() => setAction(null)} />}
      <CallSheet sessionId={open} perms={perms} onClose={() => setOpen(null)} />
    </PageShell>
  );
};
